import {ChevronDown} from 'lucide-react';
import { useState } from "react";

export default function AfricaCountry() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const accordionItems = [
    {
      label: "Kenya • Nigeria • Ghana",
      content: `**Focus:** English test readiness and transcript/document verification.  
**Tip:** Keep immunization and background checks current to avoid delays.`
    },
    {
      label: "Ethiopia • South Africa",
      content: `**Path:** Similar flows; confirm Good Standing format accepted by destination boards.`
    }
  ];

  const timeline = [
    { label: "Month 1–2", description: "Create profile, gather documents, begin exam prep" },
    { label: "Month 3–4", description: "Sit exam; submit verification docs (CGFNS/Dataflow)" },
    { label: "Month 5–6", description: "Verification complete; recruiter matching" }
  ];

  const faqItems = [
    {
      q: "Do I need English if my education was in English?",
      a: "Depends on destination rules. Some boards require specific exams; check our Resources."
    },
    {
      q: "Can I target multiple destinations?",
      a: "Yes. Choose a primary route and keep documents ready for alternates."
    }
  ];

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Africa</h1>
          <p className="text-xl text-teal-50">
            Kenya • Nigeria • Ghana • Ethiopia • South Africa
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-6 py-16 space-y-12">
        {/* Intro Text */}
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-700 leading-relaxed">
            We support routes to the USA, UK/Ireland, and GCC from Kenya, Nigeria, Ghana, Ethiopia, and South Africa.
          </p>
        </div>

        {/* Popular Routes */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Popular routes</h2>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-teal-600 mr-3 mt-1">•</span>
              <span className="text-gray-700">USA: NCLEX → CGFNS/VisaScreen</span>
            </li>
            <li className="flex items-start">
              <span className="text-teal-600 mr-3 mt-1">•</span>
              <span className="text-gray-700">UK/Ireland: NMC → CBT → OSCE</span>
            </li>
            <li className="flex items-start">
              <span className="text-teal-600 mr-3 mt-1">•</span>
              <span className="text-gray-700">GCC: Prometric/HAAD/DHA → Dataflow/TrueProfile</span>
            </li>
          </ul>
        </div>

        {/* Country Accordion */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">By country (quick guidance)</h2>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
            {accordionItems.map((item, index) => (
              <div key={index}>
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900">{item.label}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform ${
                      openIndex === index ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openIndex === index && (
                  <div className="px-6 py-4 bg-gray-50 text-gray-700 leading-relaxed whitespace-pre-line">
                    {item.content}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Typical timeline</h2>
          <div className="space-y-6">
            {timeline.map((step, index) => (
              <div key={index} className="flex items-start">
                <div className="flex-shrink-0 w-32">
                  <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold">
                    {step.label}
                  </span>
                </div>
                <p className="text-gray-700 ml-4">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
            {faqItems.map((item, index) => (
              <div key={index}>
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900">{item.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform ${
                      openFaqIndex === index ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 py-4 bg-gray-50 text-gray-700 leading-relaxed">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
          <a
            href="/signup"
            className="px-8 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors"
          >
            Register Free
          </a>
          <a
            href="/resources"
            className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-gray-400 hover:bg-gray-50 transition-colors"
          >
            See Resources
          </a>
        </div>
      </section>
    </div>
  );
}
